import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  BookMarked,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Image,
  Lock,
  Paperclip,
  Pencil,
  Ruler,
  User,
  XCircle,
} from 'lucide-react';
import { AppLayout } from '../../../components/layout/app-layout';
import { Breadcrumb } from '../../../components/layout/breadcrumb';
import { GradientAvatar } from '../../../components/shared/gradient-avatar';
import { Button } from '../../../components/ui/button';
import { Price } from '../../../components/ui/price';
import { Skeleton } from '../../../components/ui/skeleton';
import { usePermissions } from '../../../hooks/use-permissions';
import { cn } from '../../../lib/utils';
import { useAuthStore } from '../../../stores/auth.store';

export const Route = createFileRoute('/pledge-studies/$pledgeStudyId/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('pledge:view')) throw redirect({ to: '/dashboard' });
  },
  component: PledgeStudyDetailPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface FeeBreakdownJson {
  studyType: string;
  baseAmount: string;
  pointsTotal: string;
  authorityFixed: string;
  authorityOveragePct: string;
}

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  signedUrl?: string | null;
  uploadedAt: string;
  uploadedBy: { name: string };
}

interface MeasurementPoint {
  id: string;
  measurementPointTypeId: string;
  measurementPointType: { id: string; name: string; code: string; unitPrice: string } | null;
  unitPriceAtTime: string;
  quantity: number;
}

interface PledgeStudyDetail {
  id: string;
  status: string;
  totalFee: string | null;
  kalbaFee: string | null;
  beeahFee: string | null;
  feeBreakdown: unknown;
  approvalComments: string | null;
  rejectionReason: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  pledgeStudyType: { id: string; name: string; code: string; baseAmount: string };
  measurementPoints: MeasurementPoint[];
  attachments: Attachment[];
  createdBy: { id: string; name: string; email: string };
  approvedBy: { id: string; name: string; email: string } | null;
  request: {
    id: string;
    referenceNo: string;
    status: string;
    totalFee: number | null;
    createdAt: string;
    requestTypeConfig: { name: string; code: string } | null;
    account: { id: string; name: string } | null;
    contact: { fullName: string; email: string } | null;
  };
}

// ── API fetch ─────────────────────────────────────────────────────────────────

async function fetchPledgeStudy(id: string): Promise<PledgeStudyDetail> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`/api/v1/pledge-studies/${id}`, {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) throw new Error('Failed to load pledge study');
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtCurrency(val: string | null): string {
  if (!val) return '—';
  const n = Number.parseFloat(val);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const STATUS_CONFIG = {
  DRAFT: {
    label: 'Draft',
    dot: 'bg-amber-400',
    cls: 'border-amber-200 bg-amber-50 text-amber-700',
    icon: Clock,
  },
  APPROVAL_REQUEST: {
    label: 'Approval Request',
    dot: 'bg-purple-400',
    cls: 'border-purple-200 bg-purple-50 text-purple-700',
    icon: FileText,
  },
  APPROVED: {
    label: 'Approved',
    dot: 'bg-success-500',
    cls: 'border-success-200 bg-success-50 text-success-700',
    icon: CheckCircle2,
  },
  REJECTED: {
    label: 'Rejected',
    dot: 'bg-red-400',
    cls: 'border-red-200 bg-red-50 text-red-600',
    icon: XCircle,
  },
};

const PERMIT_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED_FOR_INSPECTION: 'Submitted for Inspection',
  INSPECTION_IN_PROGRESS: 'Inspection In Progress',
  PLEDGE_STUDY_REQUEST: 'Pledge Study Request',
  APPROVAL_REQUEST: 'Approval Request',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PAYMENT_PROCESS: 'Payment Process',
  PAID: 'Paid',
  PERMIT_ISSUED: 'Permit Issued',
  SENT_TO_SAP: 'Sent to SAP',
};

function FileIcon({ mimeType }: { mimeType: string }) {
  if (mimeType === 'application/pdf') return <FileText size={18} className="text-red-500" />;
  if (mimeType.startsWith('image/')) return <Image size={18} className="text-blue-500" />;
  return <FileText size={18} className="text-gray-400" />;
}

function SectionCard({
  icon,
  iconBg,
  title,
  subtitle,
  children,
  action,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <div
            className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', iconBg)}
          >
            {icon}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function PledgeStudyDetailPage() {
  const { pledgeStudyId } = Route.useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const {
    data: ps,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['pledgeStudy', pledgeStudyId],
    queryFn: () => fetchPledgeStudy(pledgeStudyId),
  });

  const isEditable = ps?.status === 'DRAFT' && can('pledge:create');

  const statusCfg = ps
    ? (STATUS_CONFIG[ps.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.DRAFT)
    : null;

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
            <div className="space-y-4">
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-48 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-56 w-full rounded-2xl" />
              <Skeleton className="h-32 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (isError || !ps) {
    return (
      <AppLayout>
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <AlertCircle size={40} className="text-error-400" />
          <p className="text-sm font-semibold text-gray-600">Pledge study not found</p>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/pledge-studies' })}>
            Back to Pledge &amp; Study
          </Button>
        </div>
      </AppLayout>
    );
  }

  const breadcrumbItems = [
    { label: 'Pledge & Study', href: '/pledge-studies' },
    { label: ps.request.referenceNo },
  ];

  const mpLineTotal = (mp: MeasurementPoint) =>
    (Number.parseFloat(mp.unitPriceAtTime) * mp.quantity).toLocaleString('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <AppLayout>
      <Breadcrumb items={breadcrumbItems} />

      {/* ── Hero header ── */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="h-1 bg-gradient-to-r from-indigo-400 via-violet-500 to-purple-400" />
        <div className="flex items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-4">
            <button
              type="button"
              onClick={() => navigate({ to: '/pledge-studies' })}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700 shrink-0 mt-0.5"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-md">
                <BookMarked size={24} className="text-white" strokeWidth={1.5} />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-1.5">
                  <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                    {ps.request.referenceNo}
                  </h1>
                  {statusCfg && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
                        statusCfg.cls,
                      )}
                    >
                      <span className={cn('h-1.5 w-1.5 rounded-full', statusCfg.dot)} />
                      {statusCfg.label}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                  <span className="flex items-center gap-1.5">
                    <BookMarked size={13} className="text-gray-400" />
                    {ps.pledgeStudyType.name}
                  </span>
                  {ps.request.requestTypeConfig && (
                    <span className="flex items-center gap-1.5">
                      <FileText size={13} className="text-gray-400" />
                      {ps.request.requestTypeConfig.name}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <Calendar size={13} className="text-gray-400" />
                    Created {fmtDate(ps.createdAt)}
                  </span>
                  {ps.approvedAt && (
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 size={13} className="text-success-500" />
                      Approved {fmtDate(ps.approvedAt)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {isEditable && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate({ to: '/pledge-studies/$pledgeStudyId/edit', params: { pledgeStudyId } })
                }
              >
                <Pencil size={14} />
                Edit
              </Button>
            )}
            <Link
              to="/permits/$permitId"
              params={{ permitId: ps.request.id }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink size={14} />
              View Permit
            </Link>
          </div>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
        {/* ── Left: content ── */}
        <div className="space-y-4">
          {/* Study Info */}
          <SectionCard
            icon={<BookMarked size={16} className="text-indigo-600" />}
            iconBg="bg-indigo-50"
            title="Study Information"
            subtitle={ps.pledgeStudyType.code}
          >
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                  Study Type
                </p>
                <p className="text-sm font-medium text-gray-800">{ps.pledgeStudyType.name}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                  Base Amount
                </p>
                <p className="text-sm font-medium text-gray-800">
                  <span className="text-[10px] text-gray-400 mr-0.5">AED</span>
                  {fmtCurrency(ps.pledgeStudyType.baseAmount)}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                  Measurement Points
                </p>
                <p className="text-sm font-medium text-gray-800">{ps.measurementPoints.length}</p>
              </div>
            </div>

            {/* Rejection reason */}
            {ps.status === 'REJECTED' && ps.rejectionReason && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-xs font-semibold text-red-600 mb-1">Rejection Reason</p>
                <p className="text-sm text-red-700 leading-relaxed">{ps.rejectionReason}</p>
              </div>
            )}

            {/* Approval comments */}
            {ps.status === 'APPROVED' && ps.approvalComments && (
              <div className="mt-4 rounded-xl border border-success-200 bg-success-50 px-4 py-3">
                <p className="text-xs font-semibold text-success-700 mb-1">Approval Comments</p>
                <p className="text-sm text-success-800 leading-relaxed">{ps.approvalComments}</p>
              </div>
            )}
          </SectionCard>

          {/* Measurement Points */}
          <SectionCard
            icon={<Ruler size={16} className="text-violet-600" />}
            iconBg="bg-violet-50"
            title="Measurement Points"
            subtitle={`${ps.measurementPoints.length} point${ps.measurementPoints.length !== 1 ? 's' : ''}`}
          >
            {ps.measurementPoints.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No measurement points recorded.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="pb-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Name
                      </th>
                      <th className="pb-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Code
                      </th>
                      <th className="pb-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Unit Price
                      </th>
                      <th className="pb-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Qty
                      </th>
                      <th className="pb-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        Line Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {ps.measurementPoints.map((mp) => (
                      <tr key={mp.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 text-gray-800 font-medium">
                          {mp.measurementPointType?.name ?? '—'}
                        </td>
                        <td className="py-2.5 text-gray-500">
                          {mp.measurementPointType?.code ?? '—'}
                        </td>
                        <td className="py-2.5 text-right text-gray-700">
                          <span className="text-[10px] text-gray-400 mr-0.5">AED</span>
                          {fmtCurrency(mp.unitPriceAtTime)}
                        </td>
                        <td className="py-2.5 text-right text-gray-700">{mp.quantity}</td>
                        <td className="py-2.5 text-right font-semibold text-gray-900">
                          <span className="text-[10px] font-normal text-gray-400 mr-0.5">AED</span>
                          {mpLineTotal(mp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          {/* Fee Breakdown */}
          {ps.totalFee && (
            <SectionCard
              icon={<CheckCircle2 size={16} className="text-success-600" />}
              iconBg="bg-success-50"
              title="Fee Summary"
              subtitle="Calculated at time of submission"
              action={
                <div className="text-right">
                  <p className="text-xs text-gray-400 mb-0.5">Total Pledge & Study Fee</p>
                  <p className="text-2xl font-bold text-gray-900">
                    <Price value={ps.totalFee} />
                  </p>
                </div>
              }
            >
              {(() => {
                const fb = ps.feeBreakdown as FeeBreakdownJson | null | undefined;
                return (
                  <div className="space-y-2">
                    {fb && (
                      <>
                        <div className="flex items-center justify-between py-1">
                          <span className="text-sm text-gray-500">Study fee</span>
                          <span className="text-sm font-medium text-gray-800">
                            <Price value={fb.baseAmount} />
                          </span>
                        </div>
                        {Number(fb.pointsTotal) > 0 && (
                          <div className="flex items-center justify-between py-1">
                            <span className="text-sm text-gray-500">Measurement points fee</span>
                            <span className="text-sm font-medium text-gray-800">
                              <Price value={fb.pointsTotal} />
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    <div className="flex items-center justify-between border-t border-gray-200 pt-2 mt-1">
                      <span className="text-sm font-semibold text-gray-800">
                        Total Pledge & Study Fee
                      </span>
                      <span className="text-base font-bold text-gray-900">
                        <Price value={ps.totalFee} />
                      </span>
                    </div>
                    {ps.beeahFee && (
                      <div className="flex items-center justify-between py-1">
                        <span className="text-sm text-gray-500">Beeah consultant fee</span>
                        <span className="text-sm font-medium text-gray-800">
                          <Price value={ps.beeahFee} />
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </SectionCard>
          )}

          {/* Attachments */}
          <SectionCard
            icon={<Paperclip size={16} className="text-violet-600" />}
            iconBg="bg-violet-50"
            title="Attachments"
            subtitle={`${ps.attachments.length} file${ps.attachments.length !== 1 ? 's' : ''} uploaded`}
          >
            {ps.attachments.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No attachments uploaded.</p>
            ) : (
              <div className="space-y-2">
                {ps.attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 hover:bg-gray-100 transition-colors"
                  >
                    <FileIcon mimeType={att.mimeType} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{att.fileName}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {fmtSize(att.fileSizeBytes)} · {att.uploadedBy.name} ·{' '}
                        {fmtDate(att.uploadedAt)}
                      </p>
                    </div>
                    {att.signedUrl ? (
                      <a
                        href={att.signedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-brand-300 hover:text-brand-500 transition-colors"
                        title="Download"
                      >
                        <Download size={14} />
                      </a>
                    ) : (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-300">
                        <Lock size={13} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        {/* ── Right sidebar ── */}
        <div className="space-y-4">
          {/* Linked permit */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-5 py-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Linked Permit
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <Link
                to="/permits/$permitId"
                params={{ permitId: ps.request.id }}
                className="flex items-center gap-2 group"
              >
                <span className="text-sm font-bold text-gray-900 group-hover:text-brand-600 transition-colors">
                  {ps.request.referenceNo}
                </span>
                <ExternalLink
                  size={12}
                  className="text-gray-400 group-hover:text-brand-500 transition-colors"
                />
              </Link>

              {ps.request.requestTypeConfig && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
                    Type
                  </p>
                  <p className="text-sm text-gray-700">{ps.request.requestTypeConfig.name}</p>
                </div>
              )}

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
                  Status
                </p>
                <p className="text-sm font-medium text-gray-700">
                  {PERMIT_STATUS_LABELS[ps.request.status] ?? ps.request.status}
                </p>
              </div>

              {ps.request.account && (
                <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                  <Building2 size={13} className="text-gray-400 shrink-0" />
                  <p className="text-sm text-gray-700 truncate">{ps.request.account.name}</p>
                </div>
              )}

              {ps.request.contact && (
                <div className="flex items-center gap-2">
                  <User size={13} className="text-gray-400 shrink-0" />
                  <p className="text-sm text-gray-700 truncate">{ps.request.contact.fullName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Created By */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-5 py-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Created By
              </p>
            </div>
            <div className="flex items-center gap-3 px-5 py-4">
              <GradientAvatar name={ps.createdBy.name} size="md" shape="circle" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{ps.createdBy.name}</p>
                <p className="text-xs text-gray-400 truncate mt-0.5">{ps.createdBy.email}</p>
              </div>
            </div>
          </div>

          {/* Approved By */}
          {ps.approvedBy && (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-4">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                  Approved By
                </p>
              </div>
              <div className="flex items-center gap-3 px-5 py-4">
                <GradientAvatar name={ps.approvedBy.name} size="md" shape="circle" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {ps.approvedBy.name}
                  </p>
                  <p className="text-xs text-gray-400 truncate mt-0.5">{ps.approvedBy.email}</p>
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
              Details
            </p>
            <div className="space-y-3">
              {[
                { label: 'Created', value: fmtDate(ps.createdAt) },
                { label: 'Last updated', value: fmtDate(ps.updatedAt) },
                ...(ps.approvedAt ? [{ label: 'Approved', value: fmtDate(ps.approvedAt) }] : []),
                {
                  label: 'Attachments',
                  value: `${ps.attachments.length} file${ps.attachments.length !== 1 ? 's' : ''}`,
                },
                { label: 'Measurement Points', value: `${ps.measurementPoints.length}` },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{label}</span>
                  <span className="text-xs font-semibold text-gray-800">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
